import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #2D3653;
  color: #C5A572;
  font-family: 'Style Script', cursive;
  height: 105px;
  position: relative;
  width: 100%;
  margin: 0;
  // border: 3px solid yellow;
  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    height: 100%;
    width: 100%;
    background: url('/images/Armon/bordure entete.png') no-repeat;
    background-size: cover;
    background-position: left;
  }

  @media (max-width: 768px) {
    height: auto;
    flex-direction: column;
    padding: 0px 0 5px 0;
    gap: 7px;

    &::before, &::after {
      display: none;
    }
  }
`;

const TimeContainer = styled.div`
  position: relative;
  width: 510px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  left: ${props => props.$isRTL ? '-50px' : '50px'};
  z-index: 2;

  @media (max-width: 768px) {
    order: 2;
    width: 100%;
    left: 0;
    height: auto;
  }
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 510px;
  height: 100%;
  background: url('/images/Armon/fd-texte-banderole.png') no-repeat center;
  background-size: cover;
  z-index: 1;

  @media (max-width: 768px) {
    width: 100%;
    height: 80px;
  }
`;

const DateText = styled.div`
  position: relative;
  z-index: 2;
  color: #C5A572;
  font-family: 'Style Script', cursive;
  font-size: 30px;
  font-weight: 800;
  flex: 1;
  text-align: center;
  background: linear-gradient(90deg, #AB8D60, #FFFFFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const TimeText = styled(DateText)`
  font-size: 55px;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const HeaderLogo = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  padding: 0 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 300px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/images/Armon/logo.png') no-repeat center;
    background-size: cover;
    z-index: 1;
  }

  img {
    max-height: 40px;
    width: auto;
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    position: relative;
    left: auto;
    transform: none;
    order: 1;
    width: 200px;
    height: 80px;
    padding: 0 20px;
    background: url('/images/Armon/bordure entete.png') no-repeat center;
    background-size: contain;
    z-index: 0;
  }
`;

const SponsorContainer = styled.div`
  position: relative;
  width: 507px;
  right: ${props => props.$isRTL ? '-50px' : '50px'};
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 40px;
  text-align: center;

  @media (max-width: 768px) {
    order: 3;
    width: 100%;
    right: 0;
    padding: 0 10px;
    height: auto;
  }
`;

const SponsorBackgroundImage = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 517px;
  height: 100%;
  background: url('/images/Armon/sponsor-texte-banderole.png') no-repeat center;
  background-size: cover;
  z-index: 1;

  @media (max-width: 768px) {
    width: 100%;
    height: 40px;
  }
`;

const SponsorText = styled.div`
  position: relative;
  z-index: 2;
  color: #C5A572;
  font-family: 'Style Script', cursive;
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(90deg, #AB8D60, #FFFFFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  width: 100%;
  transition: opacity 0.5s ease-in-out;
  opacity: ${props => props.$isVisible ? 1 : 0};

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const HeaderContainer = ({ zmanim, logo, community, sponsor, omer }) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  );
  const [currentSponsorIndex, setCurrentSponsorIndex] = useState(0);
  const [isSponsorVisible, setIsSponsorVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }));
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Slider automatique des sponsors
  useEffect(() => {
    if (!sponsor || sponsor.length <= 1) return;

    const sponsorTimer = setInterval(() => {
      // Effet de fondu
      setIsSponsorVisible(false);
      setTimeout(() => {
        setCurrentSponsorIndex((prevIndex) => 
          (prevIndex + 1) % sponsor.length
        );
        setIsSponsorVisible(true);
      }, 250); // Moitié du temps de transition
    }, 20000); // Change toutes les 5 secondes

    return () => clearInterval(sponsorTimer);
  }, [sponsor]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    }).replace(/\//g, '.');
  };

  // Récupérer le sponsor actuel
  const currentSponsor = sponsor?.[currentSponsorIndex];

  return (
    <Header>
      <TimeContainer $isRTL={isRTL}>
        <BackgroundImage />
        <DateText>
          {zmanim?.currentDate ? formatDate(zmanim.currentDate) : ''}
          <br />
          {omer?.events?.[0]?.omer}
        </DateText>
        <TimeText>{currentTime}</TimeText>
        <DateText>
          {zmanim?.todayHebrewDateFormatted || ''}
        </DateText>
      </TimeContainer>
      <HeaderLogo>
        <img src={logo} alt={community?.communityName} />
      </HeaderLogo>
      <SponsorContainer $isRTL={isRTL}>
        <SponsorBackgroundImage />
        <SponsorText $isVisible={isSponsorVisible}>
          {currentSponsor?.comment || 'Powered by Kehilati.com'}
        </SponsorText>
      </SponsorContainer>
    </Header>
  );
};

export default HeaderContainer; 